import styled from "@emotion/styled"
import { Autocomplete, TextField } from "@mui/material"
import { PointsOfInterestDto } from "api"
import { useEffect, useMemo, useState } from "react"
import { LocationIcon } from "ui"

interface RealEstateSearchInputProps {
  placeholder: string
  searchTerm: string
  onSearchChange: (value: string) => void
  list: PointsOfInterestDto[]
  onSelect?: (item: PointsOfInterestDto) => void
  /**
   * Optional id passed to the underlying MUI Autocomplete to avoid collisions
   * when rendering both desktop & mobile search inputs simultaneously.
   */
  id?: string
}

const StyledSearchInputBase = styled(TextField)`
  color: black;
  font-size: 14px;
  line-height: 20px;
  height: 48px;
  & .MuiInputBase-root {
    height: 48px;
    padding-right: 5px !important;
    display: flex;
    align-items: center;
    padding-top: 4px;
    background-color: white;
    border-radius: 28px;
      input {
        padding-left: 10px;
      }
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 12px;
    line-height: 18px;
    height: 38px;
    & .MuiInputBase-root {
      height: 38px;
      padding: 0px;
       padding-left: 5px;
      input {
        position: relative;
        padding-left: 10px;
      }
    }
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    font-size: 16px;
    line-height: 24px;
    height: 56px;
    & .MuiInputBase-root {
      padding-left: 5px;
      height: 56px;
      input {
        margin-bottom: 0px;
      }
    }
  }
  fieldset {
    border: 1px solid #153e694d !important;
    border-radius: 28px;
    outline: none;
    :hover {
      border: none;
      border-radius: 28px;
      outline: none;
    }
  }
  & .MuiInputBase-input::placeholder {
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: rgba(21, 62, 105, 1);
    position: relative;
    top: -2px;
    ${(props) => props.theme.breakpoints.up("md")} {
      font-size: 12px;
      line-height: 18px;
    }
    ${(props) => props.theme.breakpoints.up("lg")} {
      font-size: 16px;
      line-height: 24px;
    }
  }
`

export const RealEstateSearchInput: React.FC<RealEstateSearchInputProps> = ({
  placeholder,
  searchTerm,
  onSearchChange,
  list,
  onSelect,
  id
}) => {
  const [inputValue, setInputValue] = useState(searchTerm)
  // Keep a controlled selected option so we can explicitly clear it when parent triggers reset
  const [selectedOption, setSelectedOption] = useState<PointsOfInterestDto | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  // Filter options based on current input
  const options = useMemo(() => {
    // Only show options if input has at least 2 characters
    if (!inputValue.trim() || inputValue.trim().length < 2) {
      return []
    }
    const term = inputValue.trim().toLowerCase()
    return list.filter(item => {
      const name = item.Name || ""
      const shortName = item.ShortName || ""
      const cityName = item.CityName || ""
      return name.toLowerCase().includes(term) ||
        shortName.toLowerCase().includes(term) ||
        cityName.toLowerCase().includes(term)
    })
  }, [inputValue, list])

  useEffect(() => {
    setInputValue(searchTerm)
    // When parent clears the search term we also clear the selected option
    if (!searchTerm) {
      setSelectedOption(null)
      setIsOpen(false)
    }
  }, [searchTerm])

  return (
    <Autocomplete
      id={id}
      // Allow free-text search without requiring selection from dropdown
      freeSolo
      // Explicitly type the autocomplete for clarity
      value={selectedOption}
      fullWidth={true}
      open={isOpen && inputValue.trim().length >= 2 && options.length > 0}
      onOpen={() => setIsOpen(true)}
      onClose={() => {
        setIsOpen(false)
        // When closing without selecting, keep the free-text search active
        // The inputValue is already synced with parent via onInputChange
      }}
      options={options}
      getOptionLabel={(option) => {
        // Handle both object and string types since freeSolo allows string values
        if (typeof option === 'string') {
          return option
        }
        return option.ShortName || option.Name || ""
      }}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        // Always update the input value regardless of reason
        setInputValue(newInputValue)
        onSearchChange(newInputValue)
        if (newInputValue.trim().length >= 2) {
          setIsOpen(true)
        }
      }}
      onChange={(_, value) => {
        // value can be a string (freeSolo) or an object (selected from dropdown)
        if (typeof value === 'string') {
          // User typed and pressed Enter or Tab - treat as free-text search
          setInputValue(value)
          onSearchChange(value)
          setSelectedOption(null)
          setIsOpen(false)
        } else if (value) {
          // User selected from dropdown
          setSelectedOption(value)
          const label = value.ShortName || value.Name || ""
          setInputValue(label)
          onSearchChange(label)
          setIsOpen(false)
          onSelect?.(value)
        } else {
          // Cleared via UI (e.g., ESC) => also clear parent search term
          setSelectedOption(null)
          onSearchChange("")
          setIsOpen(false)
        }
      }}
      // Ensure equality check is based on stable Id instead of object reference
      isOptionEqualToValue={(option, value) => {
        // Handle string comparison for freeSolo mode
        if (typeof option === 'string' || typeof value === 'string') {
          return false
        }
        const optId = option.Id !== null && option.Id !== undefined ? option.Id.toString() : option.ShortName || option.Name
        const valId = value.Id !== null && value.Id !== undefined ? value.Id.toString() : value.ShortName || value.Name
        return optId === valId
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.Id}>
          <div>
            <div style={{ fontWeight: 600 }}>{option.ShortName || option.Name}</div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>
              {[option.Street, option.CityName].filter(Boolean).join(', ')}
            </div>
          </div>
        </li>
      )}
      popupIcon={null}
      renderInput={(params) => {
        const ariaLabel = selectedOption
          ? `${placeholder} - נבחר: ${selectedOption.ShortName || selectedOption.Name}`
          : placeholder
        return (
          <StyledSearchInputBase
            {...params}
            placeholder={inputValue ? "" : placeholder}
            variant="outlined"
            inputProps={{
              ...params.inputProps,
              'aria-label': ariaLabel,
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: <LocationIcon color="rgba(21, 62, 105, 1)" width={30} />,
            }}
          />
        )
      }}
    />
  )
}
